import {
    BucketAwtENUM,
    EstadosActivacionInstallOTEnumChoice,
    EstadosImagenChoiceEnum,
    extractIdsFromObjRes,
    useUploadFile,
} from "@/shared";
import { useUpdateOrdenTrabajo } from "@/shared/actions/erp";
import { CustomPaperBox } from "@/shared/components/ui";
import { ScrollableDialogProps } from "@/shared/components/ui/Dialogs";
import { OrdenTrabajo } from "@/shared/interfaces";
import { useUploadImageStore } from "@/store/erp";
import { useUiStore } from "@/store/ui";
import { Alert, Grid, Typography } from "@mui/material";
import dayjs from "dayjs";
import { useState } from "react";
import { toast } from "react-toastify";
import FotosVisitaMultiUploader from "./FotosVisitaMultiUploader";

const MIN = 2;
const MAX = 5;

type UploadFotosVisitaModalProps = {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    ordenTrabajo?: OrdenTrabajo;
};

/**
 * Modal de finalización: exige "Fotos de finalización" (mín 2, máx 10) con el
 * mismo estilo que ingreso/proceso. Persiste en `urls_fotos_salida.photosVisita`
 * con claves dinámicas fotoEvidencia1..N (compatibles con los lectores que
 * iteran Object.values).
 */
export default function UploadFotosVisitaModal({
    open,
    onClose,
    onSuccess,
    ordenTrabajo,
}: UploadFotosVisitaModalProps) {
    const PATH_BUCKET = `${BucketAwtENUM.IMAGE_INSTALACION}/${ordenTrabajo?.id_orden_trabajo}`;
    const setIsGlobalLoading = useUiStore((s) => s.setIsGlobalLoading);
    const setIsUploadingImgs = useUploadImageStore((s) => s.setIsUploading);
    const [files, setFiles] = useState<File[]>([]);
    const [showErrors, setShowErrors] = useState(false);
    const { handleUploadFile } = useUploadFile();
    const updateOT = useUpdateOrdenTrabajo<any>({
        enableRedirect: false,
        customSuccessMessage: "Fotos de evidencia cargadas correctamente",
    });

    const handleUploadImages = async () => {
        if (files.length < MIN) {
            setShowErrors(true);
            return;
        }
        if (
            ordenTrabajo?.requiere_cambio_ont &&
            ordenTrabajo?.estado_activacion !=
                EstadosActivacionInstallOTEnumChoice.GESTIONADA
        ) {
            return toast.error(
                "No puedes finalizar la visita. El cambio de ONT aún no ha sido gestionado por Activaciones.",
            );
        }
        setIsGlobalLoading(true);
        setIsUploadingImgs(true);
        try {
            const results = await Promise.all(
                files.map((f) => handleUploadFile(f, PATH_BUCKET)),
            );
            const photosVisita: Record<string, any> = {};
            results.forEach((r, i) => {
                photosVisita[`fotoEvidencia${i + 1}`] = {
                    photoUrl: r?.url || null,
                    photoId: r?.id_archivos || r?.url || null,
                    photoname: `EvidenciaVisita ${i + 1}`,
                    fecha_hora: dayjs().toISOString(),
                };
            });
            const urls_fotos_visita = {
                id_images_visita: extractIdsFromObjRes(results),
                photosVisita,
            };
            await updateOT.mutateAsync({
                id: ordenTrabajo?.id_orden_trabajo!,
                data: {
                    estado_imagen_visita: EstadosImagenChoiceEnum.CARGADO,
                    urls_fotos_salida: urls_fotos_visita,
                },
            });
            setFiles([]);
            setShowErrors(false);
            onClose();
            onSuccess();
        } catch (e) {
            console.error("Error subir fotos evidencia", e);
            toast.error("Error al subir las fotos de evidencia.");
        } finally {
            setIsGlobalLoading(false);
            setIsUploadingImgs(false);
        }
    };

    return (
        <ScrollableDialogProps
            open={open}
            title="Cargar Evidencia de Visita"
            minWidth="60%"
            contentNode={
                <CustomPaperBox sxPaper={{ p: 3 }}>
                    <Grid item xs={12}>
                        <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
                            Sube las <b>fotos de finalización</b> de la visita (mín{" "}
                            {MIN}, máx {MAX}).
                        </Alert>
                        <FotosVisitaMultiUploader
                            files={files}
                            setFiles={setFiles}
                            min={MIN}
                            max={MAX}
                            label="Fotos de finalización"
                        />
                        {showErrors && files.length < MIN && (
                            <Typography
                                color="error"
                                fontWeight={700}
                                sx={{ mt: 1 }}
                            >
                                Debes subir al menos {MIN} fotos de finalización.
                            </Typography>
                        )}
                    </Grid>
                </CustomPaperBox>
            }
            onClose={onClose}
            onConfirm={handleUploadImages}
            confirmTextBtn={"Guardar"}
            confirmVariantBtn="outlined"
        />
    );
}
